/**
 * Seedance 前端积分估算 —— 镜像后端 server/utils/kinovi.ts 的 seedanceCreditCost。
 * 后端为准；此处仅用于展示，两边必须保持同一套表。
 *
 * 口径（BytePlus 官方 token 计费 × 5 毛利 × 20 credits/USD）：
 *   tokens   = 像素面积 × 24fps × 计费秒数 / 1024
 *   计费秒数 = 输出时长 + (有参考视频 ? 参考视频总时长 : 0)
 * 图片数量与音频不影响价格；各宽高比等像素面积，无需宽高比因子；
 * 单任务积分向上取整到 5 的倍数（只向上，毛利只增不减）。
 */

export const REF_VIDEO_MAX_SECONDS = 15;

// 每档分辨率的像素面积（取生产实测输出尺寸，等面积覆盖所有宽高比）
const TIER_PIXELS: Record<string, number> = {
  '480': 428_544, // 864×496
  '720': 921_600, // 1280×720
  '1080': 2_073_600, // 1920×1080
  '4k': 8_294_400, // 3840×2160
};

const USD_PER_M_TOKENS = {
  pro: {
    noVideo: { low: 7, high: 7.7 },
    withVideo: { low: 4.3, high: 4.7 },
  },
  fast: {
    noVideo: { low: 5.6, high: 5.6 },
    withVideo: { low: 3.3, high: 3.3 },
  },
  // mini 官方价未公布 —— pro ×50% 暂估；仅 480/720p
  mini: {
    noVideo: { low: 3.5, high: 3.85 },
    withVideo: { low: 2.15, high: 2.35 },
  },
  // MiniMax H3 —— pro ×0.8（同分辨率/时长比 Seedance 低 20%）；
  // 自建 GPU 只按输出时长收费，参考视频不额外计 token
  h3: {
    noVideo: { low: 5.6, high: 6.16 },
    withVideo: { low: 5.6, high: 6.16 },
  },
} as const;

/** 模型名 → 计价家族（h3/fast/mini/pro） */
function priceFamily(model?: string): 'pro' | 'fast' | 'mini' | 'h3' {
  const m = String(model ?? '');
  if (m.includes('h3')) return 'h3';
  if (m.includes('fast')) return 'fast';
  if (m.includes('mini')) return 'mini';
  return 'pro';
}

export function estimateSeedanceCredits(input: {
  /** 提交给后端的模型名（含 "fast" 走 fast 价，预置） */
  model?: string;
  resolution: string;
  duration: number;
  vidCount: number;
  /** 参考视频总时长（秒，前端探测）；有视频但未知（探测中）时按 15s 上限估 */
  refVideoSeconds?: number;
  batchCount: number;
}): number {
  const family = priceFamily(input.model);
  const res = String(input.resolution ?? '720p')
    .replace(/p$/i, '')
    .toLowerCase();
  let tier = res === '4k' ? '4k' : TIER_PIXELS[res] ? res : '720';
  // fast/mini 仅 480/720 —— 超档一律按 720 计价（与后端自洽）
  if ((family === 'fast' || family === 'mini') && (tier === '1080' || tier === '4k')) tier = '720';
  // H3 最高 1080p —— 4k 按 1080p 计价（与后端 minimaxH3CreditCost 自洽）
  if (family === 'h3' && tier === '4k') tier = '1080';
  const pixels = TIER_PIXELS[tier];
  // H3 只按输出时长收费，参考视频不额外计 token
  const hasRefVideo = family !== 'h3' && input.vidCount > 0;
  const refSecs = hasRefVideo
    ? Math.min(REF_VIDEO_MAX_SECONDS, Math.max(0.5, input.refVideoSeconds && input.refVideoSeconds > 0 ? input.refVideoSeconds : REF_VIDEO_MAX_SECONDS))
    : 0;
  const billedSecs = Math.max(1, input.duration) + refSecs;
  const band = tier === '1080' || tier === '4k' ? 'high' : 'low';
  const rate = USD_PER_M_TOKENS[family][hasRefVideo ? 'withVideo' : 'noVideo'][band];
  const tokens = (pixels * 24 * billedSecs) / 1024;
  const usd = (tokens / 1_000_000) * rate;
  const perTask = Math.max(5, Math.ceil((usd * 5 * 20) / 5) * 5);
  return perTask * Math.max(1, input.batchCount);
}
